import { Accordion } from '@domgen/base-design-system-2.0/atoms/Accordion';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { RegBenefits } from '@domgen/base-design-system-2.0/atoms/RegBenefits';
import { useQuery } from '@domgen/base-design-system-2.0/hooks/useQuery';
import {
  CallUsLandingPageVariant,
  isCallUsLandingPageVariant,
  PosthogFeatureFlags,
} from '@domgen/shared/enums/featureFlags';
import { ModelNumberLookupResponse } from '@domgen/shared/models/services/api/applianceLookup';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { useFeatureFlagEnabled, usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GiftMessage } from '../../../components/GiftMessage';
import { MissingModelOrSerialNumber } from '../../../components/MissingModelOrSerialNumber/MissingModelOrSerialNumber';
import { Protection } from '../../../components/Protection/Protection';
import { brandCode } from '../../../constants/brandCode';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useModelSerialExamples } from '../../../hooks/useModelSerialExamples';
import { queryModelNumber } from '../../../services/api/queryModelNumber';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useAppState } from '../../../store/selectors/useAppState';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useApplianceLookupResults } from '../../../store/selectors/useApplianceLookupResults';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { formValidators } from '../../../utils/form/validators';
import { getSystemCode } from '../../../utils/getSystemCode';
import { getLandingPageExperiment } from '../../../utils/posthog/getLandingPageExperiment';
import { getRegistriaUrl } from '../../../utils/registria/getRegistriaUrl';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { ModelSerialLandingCallUs } from './ModelSerialLandingCallUs';
import type { ModelSerialLandingFormProps } from './ModelSerialLandingOnlineForm.types';
import { ModelSerialLandingOnlineFormDefault } from './ModelSerialLandingOnlineFormDefault';
import { landingBenefits } from './landingBenefits';

const styles = {
  heroSection: 'relative w-full bg-cover bg-center py-space-700 px-space-500 flex justify-center',
  heroOverlay: 'absolute inset-0 bg-black/30',
  heroContent: 'relative z-10 flex justify-center w-full',
  heroBox:
    'bg-(--color-bg-main-container) rounded-radius-200 p-space-700 flex flex-col gap-space-400 md:gap-space-700 w-3xl shadow-lg',
  heroRow: 'flex flex-col md:flex-row gap-space-700 w-full',
  heroLeft: 'flex flex-col gap-space-400 flex-1',
  heroRight: 'flex flex-col gap-space-400 flex-1',
  defaultCtaRow: 'flex justify-center items-center',
  defaultCtaContainer: 'w-full md:w-1/2 md:max-w-146',
  canadaLink: 'text-center',
  benefitsSection: 'w-full',
  faqSection: 'w-full bg-(--color-bg-main-container) py-space-700 px-space-500 pb-space-800',
  faqContent: 'max-w-4xl mx-auto flex flex-col items-center gap-space-500',

  faqPlaceholder:
    'w-full border border-(--color-card-border-neutral) rounded-radius-200 p-space-500',
};

type LandingPageProps = {
  heroBackground: string;
  brandImage: string;
  environment?: string | undefined;
  brandCode?: string | undefined;
};

export function ModelSerialLanding({ heroBackground, brandImage, environment }: LandingPageProps) {
  const { t } = useTranslation(['modelSerialLanding', 'common']);
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const applianceModelResults = useApplianceLookupResults();
  const urlParams = useUrlParams();
  const posthog = usePostHog();
  const [waitForSearchError, setWaitForSearchError] = useState(false);
  const displayGiftMessage = useFeatureFlagEnabled(PosthogFeatureFlags.GIFT_MESSAGE);
  const callUsLandingFlag = posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_LANDING_PAGE);
  const callUsLandingVariant = isCallUsLandingPageVariant(callUsLandingFlag)
    ? callUsLandingFlag
    : undefined;
  const showCallUsLandingPage = callUsLandingVariant === CallUsLandingPageVariant.CALLUS;

  const brandFriendlyName = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const { modelHintText, serialHintText } = useModelSerialExamples(
    import.meta.env.VITE_BRAND_CODE || brandFriendlyName,
  );
  const flagResponse = posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_LANDING_PAGE);
  const { experimentName, experimentVariant } = isCallUsLandingPageVariant(flagResponse)
    ? { experimentName: PosthogFeatureFlags.CALL_US_LANDING_PAGE, experimentVariant: flagResponse }
    : getLandingPageExperiment(posthog);
  const canadaUrl = getRegistriaUrl(brandCode, environment ?? '');
  const systemCode = getSystemCode(urlParams?.rs);
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  // Page view tracking
  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'landing-page-viewed', store.get());
  }, []);

  const { setQuery, results, loading } = useQuery<ModelNumberLookupResponse>(queryModelNumber, {
    minQueryLength: 3,
  });

  useEffect(() => {
    if (!results?.appliances) return;
    store.send({
      type: StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS,
      data: results.appliances,
    });
  }, [results]);

  const { formValues, errors, handleChange, validateAll, setFormValues } = useFormValidation(
    {
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    },
    {
      modelNumber: [
        formValidators.required(t('modelNumber.errors.required')),
        formValidators.alphanumeric(t('modelNumber.errors.invalid')),
        formValidators.maxLength(30, t('modelNumber.errors.maxLength')),
      ],
      serialNumber: [
        formValidators.required(t('serialNumber.errors.required')),
        formValidators.minLength(9, t('serialNumber.errors.minLength', { min: 9 })),
        formValidators.maxLength(20, t('serialNumber.errors.maxLength', { max: 20 })),
        formValidators.alphanumeric(t('serialNumber.errors.invalid')),
      ],
    },
  );

  useEffect(() => {
    setFormValues({
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    });
  }, [formData, setFormValues]);

  const handleContinue = () => {
    if (loading) {
      setWaitForSearchError(true);
      return;
    }

    const isValid = validateAll();

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !isValid ? 'failed' : 'success',
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'landing-continue-cta-clicked', store.get());

    if (!isValid) return;

    // Track successful model/serial submission
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'serialNumber',
      eventLabel: formValues.serialNumber,
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'modelNumber',
      eventLabel: formValues.modelNumber,
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });

    // Save model/serial data via NEXT — store will auto-fill appliance details if matched
    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.MODEL_SERIAL]: {
          [FormKey.MODEL_NUMBER]: formValues.modelNumber,
          [FormKey.SERIAL_NUMBER]: formValues.serialNumber,
        },
      },
    });
  };

  const modelSelectOptions = (results?.appliances || applianceModelResults)?.map((appliance) => ({
    label: appliance.modelNumber,
    value: appliance.modelNumber,
    description: appliance.applianceName,
  }));

  const handleModelSelect = (value: string | null) => {
    setWaitForSearchError(false);
    handleChange({ target: { name: 'modelNumber', value: value ?? '' } });
  };

  const handleModelInputChange = (value: string) => {
    setWaitForSearchError(false);
    setQuery(value);
    handleChange({ target: { name: 'modelNumber', value } });
  };

  const generateOptions = () => {
    if (modelSelectOptions?.some((option) => option.value === formValues.modelNumber)) {
      return modelSelectOptions;
    }
    if (formValues.modelNumber.length < 3 || errors.modelNumber) {
      return [];
    }
    return [
      ...(modelSelectOptions || []),
      { label: formValues.modelNumber, value: formValues.modelNumber },
    ];
  };

  const giftMessage = displayGiftMessage ? (
    <GiftMessage message="Select cooktops qualify for a free gift with accurate model & serial numbers." />
  ) : undefined;

  const formProps: ModelSerialLandingFormProps = {
    modelNumber: formValues.modelNumber,
    serialNumber: formValues.serialNumber,
    errors: {
      modelNumber: errors.modelNumber,
      serialNumber: errors.serialNumber,
    },
    loading,
    modelHintText,
    serialHintText,
    modelOptions: generateOptions(),
    waitForSearchError,
    onModelInputChange: handleModelInputChange,
    onModelSelect: handleModelSelect,
    onSerialChange: handleChange,
    onContinue: handleContinue,
    giftMessage,
  };

  return (
    <div className="flex flex-col items-center gap-space-800">
      <div className={styles.heroSection} style={{ backgroundImage: `url(${heroBackground})` }}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          {showCallUsLandingPage ? (
            <ModelSerialLandingCallUs
              systemCode={systemCode}
              experimentName={experimentName}
              experimentVariant={experimentVariant}
              brandFriendlyName={brandFriendlyName}
              formProps={formProps}
            />
          ) : (
            <div className={styles.heroBox}>
              <div className={styles.heroRow}>
                <div className={styles.heroLeft}>
                  <h1 className="text-h1">{t('heading')}</h1>
                  <p className="text-body-large">{t('intro')}</p>
                </div>

                <div className={styles.heroRight}>
                  <ModelSerialLandingOnlineFormDefault {...formProps} />
                </div>
              </div>
              {giftMessage}
              <div className={styles.defaultCtaRow}>
                <div className={styles.defaultCtaContainer}>
                  <Button onClick={handleContinue}>{t('common:continue')}</Button>
                </div>
              </div>
              <MissingModelOrSerialNumber
                appSessionID={session?.id}
                applianceCategory={applianceCategory}
                applianceName={applianceName}
                regSystemCode={systemCode}
              />
            </div>
          )}
        </div>
      </div>
      {canadaUrl && (
        <div className={styles.canadaLink}>
          <a
            href={canadaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-strong text-text-link-default underline"
          >
            {t('common:canadaLink')}
          </a>
        </div>
      )}
      <div className={styles.benefitsSection}>
        <RegBenefits heading={t('benefitsHeading')} benefits={landingBenefits} />
      </div>
      <Protection brandImage={brandImage} />
      <div className={styles.faqSection}>
        <div className={styles.faqContent}>
          <h2 className="text-h2 text-center">{t('faq.heading')}</h2>
          <p className="text-body text-center">{t('faq.intro')}</p>
          <Accordion title={t('faq.registrationRequirements.question')}>
            {t('faq.registrationRequirements.answer')}
          </Accordion>
          <Accordion title={t('faq.numberLocation.question')}>
            {t('faq.numberLocation.answer')}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
